import { useEffect, useState } from 'react';
import { CircularProgress, MenuItem, Stack, TextField } from '@mui/material';
import { useListarLocaisImpressaoDispositivoMutation } from '../api';

/**
 * Combo das impressoras da loja para a configuração LOCAL do aparelho (totem e
 * tablet). Digitar a chave à mão era fonte constante de erro — o nome tem que
 * bater exatamente com o cadastro do Sischef, e um typo só aparece quando o
 * cupom não sai.
 *
 * A lista vem do servidor de impressão e é best-effort: se ele estiver fora, o
 * backend devolve vazio. Nesse caso caímos no campo de texto em vez de travar a
 * configuração — melhor digitar do que não conseguir configurar o aparelho.
 *
 * Recebe o `pin` já validado porque o endpoint exige o mesmo PIN da tela.
 */
export function SelectLocalImpressao({
  pin,
  valor,
  onChange,
  ajuda,
}: {
  pin: string;
  valor: string;
  onChange: (valor: string) => void;
  ajuda?: string;
}): JSX.Element {
  const [listar, status] = useListarLocaisImpressaoDispositivoMutation();
  const [opcoes, setOpcoes] = useState<Array<{ value: string; label: string }> | null>(null);

  useEffect(() => {
    let ativo = true;
    void listar({ pin })
      .unwrap()
      .then((locais) => {
        if (!ativo) return;
        setOpcoes(
          locais
            .map((l) => ({
              value: (l.chave ?? '').trim(),
              label: (l.descricao ?? l.nome ?? '').trim() || (l.chave ?? '').trim(),
            }))
            .filter((o) => o.value),
        );
      })
      .catch(() => {
        if (ativo) setOpcoes([]);
      });
    return () => {
      ativo = false;
    };
  }, [pin, listar]);

  if (status.isLoading || opcoes === null) {
    return (
      <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 2 }}>
        <CircularProgress size={20} />
        <span>Buscando impressoras…</span>
      </Stack>
    );
  }

  // Sem lista (servidor de impressão fora do ar): não trava — deixa digitar.
  if (opcoes.length === 0) {
    return (
      <TextField
        label="Local de impressão"
        value={valor}
        onChange={(e) => onChange(e.target.value)}
        fullWidth
        helperText="Não foi possível listar as impressoras. Digite a chave do local cadastrado no Sischef."
      />
    );
  }

  // Valor gravado que não está mais na lista continua selecionável — senão ele
  // sumiria em silêncio e o aparelho passaria a imprimir em outro lugar.
  const opcoesComSalvo =
    valor && !opcoes.some((o) => o.value === valor) ? [{ value: valor, label: valor }, ...opcoes] : opcoes;

  return (
    <TextField
      select
      label="Local de impressão"
      value={valor}
      onChange={(e) => onChange(e.target.value)}
      fullWidth
      helperText={ajuda ?? 'Impressora onde saem os cupons deste aparelho.'}
    >
      <MenuItem value="">Não configurado</MenuItem>
      {opcoesComSalvo.map((o) => (
        <MenuItem key={o.value} value={o.value}>
          {o.label}
        </MenuItem>
      ))}
    </TextField>
  );
}
